import React, { createContext, useContext, useCallback, useEffect, useRef, useState } from 'react';
import { useAuth } from './AuthContext';
import { useNotifications } from './NotificationContext';
import { apiJson } from './apiClient';
import { roundMoney2 } from './utils/money';
import { isLowStock, lowStockThreshold } from './utils/lowStock';

const ProductContext = createContext();
const LOW_STOCK_ALERT_CACHE_KEY = 'cbonline.lowstock.alerts.v1';

const emptyProductsApi = {
  products: [],
  pendingInventory: [],
  pendingProducts: [],
  inventoryLoading: false,
  addProduct: async () => ({ status: 'pending' }),
  approveProduct: async () => {},
  rejectProduct: async () => {},
  updateProduct: async () => {},
  deleteProduct: async () => {},
  decrementProductQuantity: () => {},
  refreshProducts: async () => {},
};

export function useProducts() {
  const ctx = useContext(ProductContext);
  if (!ctx) {
    if (process.env.NODE_ENV === 'development') {
      // eslint-disable-next-line no-console
      console.warn('useProducts called outside ProductProvider');
    }
    return emptyProductsApi;
  }
  return ctx;
}

function InnerProductProvider({ children }) {
  const { user } = useAuth();
  const { push: notify } = useNotifications();
  const [products, setProducts] = useState([]);
  const [pendingInventory, setPendingInventory] = useState([]);
  const [inventoryLoading, setInventoryLoading] = useState(true);
  const pendingRef = useRef([]);
  pendingRef.current = pendingInventory;

  useEffect(() => {
    if (!user || inventoryLoading) return;
    const uid = String(user?.id || user?.email || 'anon');
    const cacheKey = `${LOW_STOCK_ALERT_CACHE_KEY}:${uid}`;

    let cache = {};
    try {
      const raw = window.localStorage.getItem(cacheKey);
      cache = raw ? JSON.parse(raw) : {};
    } catch {
      cache = {};
    }

    const lowItems = (Array.isArray(products) ? products : []).filter(isLowStock);
    const lowIds = new Set(lowItems.map((p) => String(p.id)));
    const freshAlerts = [];

    for (const item of lowItems) {
      const id = String(item.id);
      const qty = Number(item.quantity) || 0;
      if (cache[id] !== qty) {
        freshAlerts.push(item);
      }
      cache[id] = qty;
    }

    for (const key of Object.keys(cache)) {
      if (!lowIds.has(key)) delete cache[key];
    }

    try {
      window.localStorage.setItem(cacheKey, JSON.stringify(cache));
    } catch {
      /* ignore */
    }

    if (freshAlerts.length === 0) return;
    const preview = freshAlerts.slice(0, 3).map((p) => `${p.name} (${Number(p.quantity) || 0})`);
    const more = freshAlerts.length > 3 ? ` +${freshAlerts.length - 3} more` : '';
    const firstLimit = lowStockThreshold(freshAlerts[0]);
    void notify({
      title: freshAlerts.length === 1 ? 'Low stock alert' : `Low stock alerts (${freshAlerts.length})`,
      body:
        freshAlerts.length === 1
          ? `${freshAlerts[0].name} is low (${Number(freshAlerts[0].quantity) || 0} left, limit ${firstLimit}).`
          : `${preview.join(', ')}${more}. Check low stock page.`,
      navigateTo: 'stock-alerts',
    });
  }, [products, user, inventoryLoading, notify]);

  const refreshProducts = useCallback(async (options = {}) => {
    setInventoryLoading(true);
    try {
      const syncPos = options?.syncPos === true;
      const data = await apiJson(syncPos ? '/products?sync_pos=1' : '/products');
      const list = Array.isArray(data.products) ? data.products : [];
      setProducts(
        list.map((p) => ({
          ...p,
          purchasingPrice: roundMoney2(p.purchasingPrice),
          sellingPrice: roundMoney2(p.sellingPrice),
        }))
      );
      const pend = Array.isArray(data.pending) ? data.pending : [];
      setPendingInventory(
        pend.map((r) => ({
          ...r,
          product: r.product
            ? {
                ...r.product,
                purchasingPrice: roundMoney2(r.product.purchasingPrice),
                sellingPrice: roundMoney2(r.product.sellingPrice),
              }
            : r.product,
        }))
      );
    } catch (err) {
      notify({
        title: 'Could not load inventory',
        body: err?.message || 'Check your connection and try again.',
        navigateTo: 'stock-list',
      });
      setProducts([]);
      setPendingInventory([]);
    } finally {
      setInventoryLoading(false);
    }
  }, [notify]);

  useEffect(() => {
    if (!user) {
      setProducts([]);
      setPendingInventory([]);
      setInventoryLoading(false);
      return;
    }
    void refreshProducts();
  }, [user, refreshProducts]);

  const addProduct = async (product, user) => {
    const submittedBy = user?.name || user?.email || 'Unknown user';

    try {
      const data = await apiJson('/products', {
        method: 'POST',
        body: JSON.stringify({
          name: product.name,
          category: product.category ?? '',
          quantity: Number(product.quantity) || 0,
          purchasingPrice: roundMoney2(Number(product.purchasingPrice) || 0),
          sellingPrice: roundMoney2(Number(product.sellingPrice) || 0),
          lowStockLimit: Number(product.lowStockLimit) || 0,
          unit: product.unit || 'pcs',
          expiryDate: product.expiryDate || null,
          batchLot: product.batchLot || null,
          barcode: product.barcode || null,
          partCode: product.partCode || null,
          oemNumber: product.oemNumber || null,
          vehicleType: product.vehicleType || null,
          compatibleModels: product.compatibleModels || product.applicableTo || null,
          brandName: product.brandName || null,
          supplierName: product.supplierName || null,
          branchId: product.branchId != null && product.branchId !== '' ? Number(product.branchId) : undefined,
        }),
      });
      await refreshProducts();

      if (data.status === 'pending') {
        notify({
          title: 'New product pending',
          body: `${submittedBy} added "${product.name}" (${Number(product.quantity) || 0} units) — awaiting approval.`,
          navigateTo: 'stock-list',
        });
        return { status: 'pending' };
      }

      const qty = Number(product.quantity) || 0;
      notify({
        title: qty > 0 ? 'Product added' : 'Product registered',
        body:
          qty > 0
            ? `"${product.name}" is in stock (${qty} units).`
            : `"${product.name}" has been saved to your catalog. Add opening stock or receive a purchase to stock it.`,
        navigateTo: qty > 0 ? 'stock-list' : 'stock-list',
      });
      return { status: 'approved' };
    } catch (err) {
      notify({
        title: 'Product not saved',
        body: err?.message || 'Try again.',
        navigateTo: 'stock-add',
      });
      throw err;
    }
  };

  const approveProduct = async (requestId, approverUser) => {
    const req = pendingRef.current.find((r) => String(r.requestId) === String(requestId));
    const id = parseInt(String(requestId), 10);
    if (!Number.isFinite(id)) return;

    try {
      await apiJson(`/products/${id}/approve`, { method: 'POST', body: '{}' });
      await refreshProducts();
      const name = req?.product?.name || 'Product';
      const qty = Number(req?.product?.quantity) || 0;
      const approver = approverUser?.name || approverUser?.email || 'Approver';
      notify({
        title: 'Product approved',
        body: `"${name}" is now active (${qty} units). Approved by ${approver}.`,
        navigateTo: 'stock-list',
      });
    } catch (err) {
      notify({
        title: 'Approve failed',
        body: err?.message || 'Try again.',
        navigateTo: 'stock-list',
      });
      throw err;
    }
  };

  const rejectProduct = async (requestId) => {
    const req = pendingRef.current.find((r) => String(r.requestId) === String(requestId));
    const id = parseInt(String(requestId), 10);
    if (!Number.isFinite(id)) return;

    try {
      await apiJson(`/products/${id}/reject`, { method: 'POST', body: '{}' });
      await refreshProducts();
      if (req?.product) {
        notify({
          title: 'Request rejected',
          body: `New product request "${req.product.name}" was rejected.`,
          navigateTo: 'stock-list',
        });
      }
    } catch (err) {
      notify({
        title: 'Reject failed',
        body: err?.message || 'Try again.',
        navigateTo: 'stock-list',
      });
      throw err;
    }
  };

  const updateProduct = async (productId, payload) => {
    const id = parseInt(String(productId), 10);
    if (!Number.isFinite(id)) return;
    try {
      await apiJson(`/products/${id}`, {
        method: 'PATCH',
        body: JSON.stringify({
          name: payload.name,
          category: payload.category ?? '',
          quantity: Number(payload.quantity) || 0,
          purchasingPrice: roundMoney2(Number(payload.purchasingPrice) || 0),
          sellingPrice: roundMoney2(Number(payload.sellingPrice) || 0),
          lowStockLimit: Number(payload.lowStockLimit) || 0,
          unit: payload.unit || 'pcs',
          expiryDate: payload.expiryDate || null,
          batchLot: payload.batchLot || null,
          barcode: payload.barcode || null,
          partCode: payload.partCode || null,
          oemNumber: payload.oemNumber || null,
          vehicleType: payload.vehicleType || null,
          compatibleModels: payload.compatibleModels || payload.applicableTo || null,
          brandName: payload.brandName || null,
          supplierName: payload.supplierName || null,
          branchId: payload.branchId != null && payload.branchId !== '' ? Number(payload.branchId) : undefined,
        }),
      });
      await refreshProducts();
      notify({
        title: 'Product updated',
        body: `"${payload.name}" has been updated successfully.`,
        navigateTo: 'stock-list',
      });
    } catch (err) {
      notify({
        title: 'Update failed',
        body: err?.message || 'Could not update product.',
        navigateTo: 'stock-list',
      });
      throw err;
    }
  };

  const deleteProduct = async (productId, productName = 'Product') => {
    const id = parseInt(String(productId), 10);
    if (!Number.isFinite(id)) return;
    try {
      await apiJson(`/products/${id}`, { method: 'DELETE' });
      await refreshProducts();
      notify({
        title: 'Product deleted',
        body: `"${productName}" has been removed from stock.`,
        navigateTo: 'stock-list',
      });
    } catch (err) {
      notify({
        title: 'Delete failed',
        body: err?.message || 'Could not delete product.',
        navigateTo: 'stock-list',
      });
      throw err;
    }
  };

  const decrementProductQuantity = (productId, qtySold) => {
    const q = Number(qtySold);
    if (!q || q < 1) return;
    setProducts((prev) =>
      prev.map((p) => {
        if (p.id !== productId && String(p.id) !== String(productId)) return p;
        const next = Math.max(0, (Number(p.quantity) || 0) - q);
        return { ...p, quantity: next };
      })
    );
  };

  return (
    <ProductContext.Provider
      value={{
        products,
        pendingInventory,
        pendingProducts: pendingInventory,
        inventoryLoading,
        addProduct,
        approveProduct,
        rejectProduct,
        updateProduct,
        deleteProduct,
        decrementProductQuantity,
        refreshProducts,
      }}
    >
      {children}
    </ProductContext.Provider>
  );
}

export function ProductProvider({ children }) {
  return <InnerProductProvider>{children}</InnerProductProvider>;
}
